import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ServerApi } from './server.api';
import { IPlacemark, PlacemarkData } from 'src/app/Store/placemarks.state/placemarks.model';
import { SetPlacemark } from 'src/app/Store/placemarks.state/placemarks.actions';
import { Set3DModel } from 'src/app/Store/models.state/models.actions';
import { I3DModel } from 'src/app/Store/models.state/models.model';
import { environment } from 'src/environments/environment';
import { ApiTools } from './api.tools';
import { UserDetailsService } from '../user-details.service';
import { ISite } from 'src/app/Store/sites.state/sites.model';
import { CommonUtilitySvc } from '../common-utility.service';
import { SetTourPlacemark } from 'src/app/Store/baseTour.state/baseTour.actions';
import { IBaseTour } from 'src/app/Store/baseTour.state/baseTour.model';

@Injectable()
export class DispatcherSseApiSvc {
  constructor(private readonly serverApi: ServerApi, private readonly store: Store, private readonly userDetailsService: UserDetailsService,
    private readonly commonUtilitySvc: CommonUtilitySvc
  ) { }

  // Below method is used only for UI updates from API Gateway Dispatcher Component.
  public getSseUpdates(isSiteChanged: boolean): void {
    if (isSiteChanged) {
      this.serverApi.stopEeventsFromDispatcher();
    }
    // Send the externalId of the active site to the dispatcher to get the updates for that site.
    const activeSite = this.store.selectSnapshot((state: any) => state.StateApp.activeSite) as ISite;
    const externalIds = encodeURIComponent(JSON.stringify(activeSite.externalId.map(({ externalId, application }) => ({ externalId, application }))));
    
    const eventStreamURL: string = `${environment.dispatcherUrl}?tenantId=${this.userDetailsService.getCompany()}&siteId=${ApiTools.defaultSiteId}&siteExternalIds=${externalIds}`;
    this.serverApi.startEventsFromDispatcher(eventStreamURL).subscribe({
      next: (response: any) => {
        const messages: any[] = JSON.parse(response.data);
        const models: I3DModel[] = this.store.selectSnapshot<I3DModel[]>((state: any) => state.StateModels.models3D);
        if (messages && messages.length > 0) {
          console.log(`Got : ${messages.length} SSE Messages from Dispatcher`);
          for (let i = 0; i < messages.length; i++) {
            switch (messages[i].objectType) {
              case 'PM':
                this.updatePlacemarks(messages[i]);
                break;
              case '3D':
                this.update3DModels(messages[i], models);
                break;
            }
          }
        }
      },
      error: (error: any) => {
        console.log('Error receiving sse updates from dispatcher', error);
      }
    });
  }

  private updatePlacemarks(message: any): void {
    const isInTourMode = this.commonUtilitySvc.isInTourMode();
    const placemarks: IPlacemark[] = isInTourMode
      ? this.store.selectSnapshot<IPlacemark[]>((state: any) => state.StateBaseTour?.tourPlacemarks) || []
      : this.store.selectSnapshot<IPlacemark[]>((state: any) => state.StatePlacemarks.placemarks) || [];
    let existingPmIndex: number = placemarks.findIndex((pm: IPlacemark) => {
      return pm.externalId.some(extId => extId.externalId === message.externalId && extId.application === message.application);
    });
    if (existingPmIndex != -1) {
      console.log('SSE Message for PM : ', message);
      if (message.status) {
        placemarks[existingPmIndex].selectedStatus = message.status - 1;
      }
      if (message.eventInfo) {
        placemarks[existingPmIndex].eventInfo = message.eventInfo;
      }
      if (message.url) {
        placemarks[existingPmIndex].url = message.url;
      }
      if (message.name) {
        placemarks[existingPmIndex].name = message.name;
      }
      if (message.placemarkData) {
        this.updatePlacemarkData(placemarks[existingPmIndex], JSON.parse(message.placemarkData), message.updatePolicy);
      }
      if (message.x) {
        placemarks[existingPmIndex].positionProperty.x = parseFloat(message.x);
      }
      if (message.y) {
        placemarks[existingPmIndex].positionProperty.y = parseFloat(message.z);
      }
      if (message.z) {
        placemarks[existingPmIndex].positionProperty.z = parseFloat(message.y);
      }

      if (isInTourMode) {
        this.serverApi.storeDispatch(new SetTourPlacemark(placemarks[existingPmIndex], true));
        
        // Check if we need to update the current step placemark
        const baseTour: IBaseTour = this.store.selectSnapshot<IBaseTour>((state: any) => state.StateBaseTour?.baseTour);
        const currentStepNum = this.store.selectSnapshot<number | null>((state: any) => state.StateBaseTour?.currentStepID);
        
        if (baseTour?.steps && currentStepNum !== null) {
          const currentStep = baseTour.steps[currentStepNum - 1];
          const isCurrentStepPm =  currentStep?.placemarks?.includes(placemarks[existingPmIndex].id);
          if (isCurrentStepPm) {
            this.serverApi.storeDispatch(new SetPlacemark(placemarks[existingPmIndex]));
          }
        }
      } else {
        this.serverApi.storeDispatch(new SetPlacemark(placemarks[existingPmIndex]));
      }
    }
  }

  private update3DModels(message: any, models: I3DModel[]): void {
    let existingModelIndex: number = models.findIndex((model: I3DModel) => model.externalId && model.externalId.length > 0 && model.externalId[0].externalId == message.externalId);
    if (existingModelIndex != -1) {
      console.log('SSE Message for 3D : ', message);
      if (message.x) {
        models[existingModelIndex].latitude = message.x;
      }
      if (message.y) {
        models[existingModelIndex].longitude = message.y;
      }
      if (message.z) {
        models[existingModelIndex].altitude = message.z;
      }
      if (message.rotation) {
        models[existingModelIndex].rotation = message.rotation;
      }
      if (message.speed) {
        models[existingModelIndex].movementParams.speed = message.speed;
      }
      if (message.autoRotate) {
        models[existingModelIndex].movementParams.autoRotate = message.autoRotate;
      }
      if (message.referenceId) {
        models[existingModelIndex].movementParams.referenceId = message.referenceId;
      }
      this.serverApi.storeDispatch(new Set3DModel(models[existingModelIndex]));
    }
  }

  private updatePlacemarkData(existingPm: IPlacemark, placemarkData: any, updatePolicy: String): void {
    const UPDATE_POLICY_REPLACE = "replace";
    if (updatePolicy && updatePolicy !== "" && updatePolicy === UPDATE_POLICY_REPLACE) {
      if (!existingPm.placemarkData) {
        existingPm.placemarkData = [];
      }
      const filteredPms = existingPm.placemarkData.filter(pm => !pm.replaceVariable);
      existingPm.placemarkData = filteredPms;
      for (const pmData of placemarkData) {
        let placemarkData: PlacemarkData = {
          variableId: pmData.variableName,
          name: pmData.variableName,
          value: pmData.variableValue,
          replaceVariable: true
        };
        existingPm.placemarkData.push(placemarkData);
      }
    } else {
      for (const pmData of placemarkData) {
        let selectedPmData: PlacemarkData = existingPm.placemarkData.find((d: PlacemarkData) => d.variableId === pmData.variableName);
        if (selectedPmData) {
          selectedPmData.value = pmData.variableValue;
        } else {
          let placemarkData: PlacemarkData = {
            variableId: pmData.variableName,
            name: pmData.variableName,
            value: pmData.variableValue,
            replaceVariable: true
          };
          existingPm.placemarkData.push(placemarkData);
        }
      }
    }
  }
}
